import { useCallback } from 'react';
import type { ArrayElement, NotAny } from '@mews/std';
import type { Prefix as BasePrefix } from '../../utils/createPrefixes';
import { useLocalizeText, useLocalizeTextOrNull } from '../text';

interface EnumLocalizationBase {
    prefixes: Record<string, BasePrefix>;
}

declare global {
    namespace Mews {
        // workaround to override an existing property on an interface
        interface EnumLocalization extends EnumLocalizationBase {}
    }
}

/**
 * Extracts the prefix of a generated enum.
 *
 * @example
 * GetEnumPrefix<TitlePrefix> -> 'TitlePrefix'
 */
export type GetEnumPrefix<T> = {
    [K in keyof Mews.EnumLocalization['prefixes']]: T extends Mews.EnumLocalization['prefixes'][K]['valueType']
        ? K
        : ArrayElement<T> extends never
        ? never
        : ArrayElement<T> extends Mews.EnumLocalization['prefixes'][K]['valueType']
        ? K
        : never;
}[keyof Mews.EnumLocalization['prefixes']];

export const useLocalizeEnum = () => {
    const localizeText = useLocalizeText();
    const localizeTextOrNull = useLocalizeTextOrNull();

    return useCallback(
        <
            Prefix extends keyof Mews.EnumLocalization['prefixes'],
            Value extends Mews.EnumLocalization['prefixes'][Prefix]['valueType'],
        >(
            prefix: Prefix,
            value: NotAny<Value>
        ) => {
            const translationWithPrefix = localizeTextOrNull({
                textKey: `${prefix}${value}` as Mews.TextKey,
            });

            return translationWithPrefix ?? localizeText({ textKey: value as Mews.TextKey });
        },
        [localizeText, localizeTextOrNull]
    );
};
